/**
 * Receipt-style money summary shared by the portal payment surfaces.
 *
 * The storefront cart keeps its own summary: it renders per-promo savings
 * sub-lines that only make sense there, and folding those in would make
 * this component carry a shape no payment screen uses. The visual language
 * is deliberately the same, which is the part that matters to a customer.
 *
 * PD-15688 — the pay screens previously stated a "Total Payment" that was
 * already net of applied credit and then described the credit in a sentence
 * ("Includes $2.00 of applied credit"), which reads as though the credit is
 * inside the figure rather than deducted from it. They also quoted a
 * "Total charge" that the Pay button did not match. The layout here is the
 * one customers already know from a checkout: each amount on the right,
 * deductions carried as their own negative line, the processing fee added
 * last (it is sized on what the card actually moves, so it can only be
 * stated after the credit), and one grand total that the button repeats.
 *
 * Presentation only — every figure is computed by the caller. The fee
 * arithmetic settled by PD-15174 / PD-15177 / PD-15441 stays where it is.
 */

import { useTranslation } from '@supply-mover-2-0/web-ui';
import React from 'react';
import { formatCurrency } from '../lib/formatCurrency';

export interface PaymentSummaryLine {
	/** Row label, already meaningful to a customer. */
	label: string;
	/**
	 * Signed amount. Negative values render in the success colour with an
	 * explicit minus, matching the cart's promo/savings lines.
	 */
	amount: number;
	/**
	 * Drop the row when the amount rounds to zero. Deductions and optional
	 * extras use this; the payment amount itself is always shown.
	 */
	hideWhenZero?: boolean;
	/** Muted helper line beneath the row (e.g. why a fee is absent). */
	hint?: string;
}

interface PaymentSummaryProps {
	lines: Array<PaymentSummaryLine>;
	/** The figure the customer is actually charged. */
	total: number;
	/** Grand-total label. Defaults to "Total charge". */
	totalLabel?: string;
	className?: string;
}

/**
 * A single label/amount row. Negative amounts are rendered with a real
 * minus sign in the success colour so a deduction reads as a deduction at
 * a glance rather than as another charge.
 */
const SummaryRow: React.FC<{ line: PaymentSummaryLine }> = ({ line }) => {
	const { t } = useTranslation();
	const isDeduction = line.amount < 0;

	return (
		<div>
			<div
				className={`flex items-center justify-between ${
					isDeduction ? 'text-success' : 'text-brand-muted'
				}`}
			>
				<span>{t(line.label)}</span>
				<span>
					{isDeduction ? '−' : ''}
					{t(formatCurrency(Math.abs(line.amount)) ?? '')}
				</span>
			</div>
			{line.hint != null && (
				<p className="text-xs text-brand-muted mt-0.5">{t(line.hint)}</p>
			)}
		</div>
	);
};

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
	lines,
	total,
	totalLabel,
	className,
}) => {
	const { t } = useTranslation();

	const visible = lines.filter(
		(line) => line.hideWhenZero !== true || Math.abs(line.amount) >= 0.005,
	);

	return (
		<div className={`space-y-1 text-sm ${className ?? ''}`}>
			{visible.map((line, index) => (
				<SummaryRow key={`${line.label}-${index}`} line={line} />
			))}
			<div className="flex items-center justify-between font-semibold text-default pt-2 border-t border-brand-border">
				<span>{t(totalLabel ?? 'Total charge')}</span>
				<span>{t(formatCurrency(total) ?? '')}</span>
			</div>
		</div>
	);
};
